import { useAtom } from 'jotai';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useIsSearchingByGtin } from 'common/hooks/useIsSearchingByGtin';
import { useKeydown } from 'common/hooks/useKeydown';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { KeyboardOff as KeyboardOffIcon } from 'common/icons/KeyboardOff';
import { KeyboardOn as KeyboardOnIcon } from 'common/icons/KeyboardOn';
import { displayVirtualKeyboardAtom } from 'common/state/virtualKeyboardState';

import { warehouseCommonRoute } from '..';

const SearchPanel = styled(ContentContainer)`
  display: flex;
  gap: 16px;
  padding-top: 0;
  padding-bottom: 12px;
`;

const StyledKeyboardOnIcon = styled(KeyboardOnIcon)`
  fill: ${color.textSubdued};
`;

const StyledKeyboardOffIcon = styled(KeyboardOffIcon)`
  fill: ${color.textSubdued};
`;

const useKeydownWithToggle = (onKeydown: (event: KeyboardEvent) => void) => {
  const [isEnabled, setIsEnabled] = useState(false);

  useKeydown((event) => {
    if (isEnabled) {
      onKeydown(event);
    }
  });

  return {
    enableKeydownCapture: () => setIsEnabled(true),
    disableKeydownCapture: () => setIsEnabled(false),
  };
};

type Props = {
  children: (searchQuery: string, isSearchingByGtin: boolean) => React.ReactElement;
  onClick?: () => void;
  onChange?: () => void;
  onClear?: () => void;
  keydownCaptureDisabled?: boolean;
};

export const ItemSearchInput = ({
  children,
  onClick,
  onChange,
  onClear,
  keydownCaptureDisabled,
}: Props) => {
  const formatMessage = useFormatMessage();
  const [displayVirtualKeyboard, setDisplayVirtualKeyboard] = useAtom(displayVirtualKeyboardAtom);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { searchQuery = '' } = warehouseCommonRoute.useSearch();
  const setSearch = useSetSearch(warehouseCommonRoute);

  const { enableKeydownCapture, disableKeydownCapture } = useKeydownWithToggle((event) => {
    const warehouseSearchInput = document.getElementById('warehouse-search-input');
    const isModifierPressed = event.altKey || event.ctrlKey || event.metaKey;

    const eventKey = event.key;

    if (
      eventKey?.length === 1 &&
      !isModifierPressed &&
      document.activeElement !== warehouseSearchInput &&
      !keydownCaptureDisabled
    ) {
      setSearch({ searchQuery: searchQuery + eventKey });
    }
  });

  const isMobile = useMatchMedia('maxMd');
  const isSearchingByGtin = useIsSearchingByGtin(searchQuery);

  const handleToggle = () => {
    setDisplayVirtualKeyboard(!displayVirtualKeyboard);
    searchInputRef.current?.focus();
  };

  return (
    <>
      <SearchPanel>
        <SearchInput
          onClick={onClick}
          placeholder={formatMessage('warehouse.common.search_items')}
          autoFocus={true}
          value={searchQuery}
          onChange={(value) => {
            if (onChange) {
              onChange();
            }
            setSearch({ searchQuery: value });
          }}
          onFocus={disableKeydownCapture}
          onBlur={enableKeydownCapture}
          data-test-id="search-items-input"
          inputMode={displayVirtualKeyboard ? 'text' : 'none'}
          ref={searchInputRef}
          onClear={() => {
            if (onClear) onClear();
          }}
          data-barcode={true}
        />
        {isMobile && (
          <IconButton onClick={handleToggle} variant="neutral" size="small">
            {displayVirtualKeyboard ? <StyledKeyboardOnIcon /> : <StyledKeyboardOffIcon />}
          </IconButton>
        )}
      </SearchPanel>
      {children(searchQuery, isSearchingByGtin)}
    </>
  );
};
